import React from 'react';
import { useTranslations } from 'next-intl';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { AttributeTilePicker } from '@/ui/components/pages/ProductDetail/attribute-tile-picker/attribute-tile-picker';
import { BundleHostSlot } from '@/ui/components/pages/ProductDetail/types/bundle-host-slot';
import { ParsedHardwareProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedHardwareProductVariant';
import { ParsedProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedProductVariant';
import { BundleProduct } from '@/ui/services/BundleProductService';
import { BundleOptionPicker } from './bundle-option-picker';

// Module level so the tag renderer is not recreated as a nested component on every render.
const ATTRIBUTE_VALUE_TAGS = {
  strong: (chunks: React.ReactNode) => (
    <strong className="font-bold text-neutral-100">{chunks}</strong>
  ),
};

export interface BundleSlotRowProps {
  product: BundleProduct;
  selectedVariant?: ParsedProductVariant;
  onSelect: (variant: ParsedHardwareProductVariant) => void;
  // The host's own pickers drive its slot, so its row never writes the bundle selection.
  isHost?: boolean;
  hostSlot?: BundleHostSlot;
}

/**
 * Compact row for one bundle item: thumbnail of the selected variant, name, the
 * selected value and small option tiles. The host product's row shows the host's
 * own options as far as the slot sells them, every other configurable row the
 * options of its slot. The selected variant is the one the cart gets.
 */
export function BundleSlotRow({
  product,
  selectedVariant,
  onSelect,
  isHost = false,
  hostSlot,
}: Readonly<BundleSlotRowProps>) {
  const t = useTranslations('BundleVariants');
  const shownVariant = selectedVariant ?? product.defaultVariant;
  // A host variant the slot does not sell would end up as the slot default in the cart,
  // so it gets no tile and the row shows what the cart gets.
  const hostTiles =
    hostSlot?.tiles.filter((tile) => !!product.getPurchasableVariantBySKU(tile.type)) ?? [];
  const hostTile = hostTiles.find((tile) => tile.type === shownVariant?.sku);
  const isHostDriven = isHost || !!hostSlot;
  const isConfigurable = hostTiles.length > 0 || product.isSelectable();
  const attribute = hostSlot && hostTile ? hostSlot.attributeLabel : product.getSlotAttribute();
  const slotValue = shownVariant ? product.getSelectedValueLabel(shownVariant) : undefined;
  const value = hostTile?.name ?? slotValue;

  return (
    <div className="flex items-center gap-3 py-2" data-testid="bundle-slot-row">
      <div
        className="h-9 w-9 shrink-0 overflow-hidden rounded-lg border border-neutral-15 bg-white"
        data-testid="bundle-slot-thumbnail"
      >
        <OptimizedImage
          image={shownVariant?.images[0]}
          sizes="36px"
          width={36}
          height={36}
          className="h-full w-full object-contain p-0.5"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-bold">{product.name}</div>
        {isConfigurable && value && (
          <div className="text-sm text-neutral-70" data-testid="bundle-slot-attribute">
            {t.rich('attributeValue', { ...ATTRIBUTE_VALUE_TAGS, attribute: t(attribute), value })}
          </div>
        )}
      </div>
      {isHostDriven ? (
        <AttributeTilePicker
          options={hostTiles}
          selected={hostTile}
          onSelect={(tile) => hostSlot?.selectSku(tile.type)}
          size="small"
        />
      ) : (
        <BundleOptionPicker
          product={product}
          selectedSku={shownVariant?.sku}
          onSelect={onSelect}
          size="small"
        />
      )}
    </div>
  );
}
